import React, { useEffect, useState } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import API from "../services/api"
import { getContestByMatch, joinContest } from "../services/contestService"
import { getUser } from "../utils/storage"
import ContestList from "./ContestList"
import { parseMatchTime } from "../utils/dateUtils"
import BannerSlider from "../components/BannerSlider"



const MatchSkeleton = () => (
  <div className="bg-theme-bg text-theme-text min-h-screen pb-20">
    <div className="p-4 flex justify-between items-center border-b border-theme-border">
      <div className="w-6 h-6 bg-theme-card border border-theme-border rounded"></div>
      <div className="text-center space-y-2">
        <div className="w-32 h-4 bg-theme-card border border-theme-border rounded mx-auto animate-pulse"></div>
        <div className="w-20 h-3 bg-theme-card rounded mx-auto animate-pulse"></div>
      </div>
      <div className="w-6 h-6 bg-theme-card border border-theme-border rounded"></div>
    </div>
    <div className="p-4 space-y-6">
      {[1, 2, 3].map(i => (
        <div key={i} className="bg-theme-card h-44 rounded-theme border border-theme-border relative overflow-hidden shadow-theme-sm">
          <div className="p-5 space-y-4">
            <div className="flex justify-between">
              <div className="w-24 h-6 bg-theme-bg border border-theme-border rounded-lg"></div>
              <div className="w-20 h-8 bg-theme-bg border border-theme-border rounded-xl"></div>
            </div>
            <div className="w-full h-12 bg-theme-bg border border-theme-border/50 rounded-2xl"></div>
            <div className="flex justify-between">
              <div className="w-32 h-4 bg-theme-bg rounded"></div>
              <div className="w-24 h-4 bg-theme-bg rounded"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
)

const MatchDetails = () => {
  const {id} = useParams()
    const matchId = id
    const navigate = useNavigate()
    const location = useLocation()
    const teamIdToJoin = new URLSearchParams(location.search).get("teamId")

    const [match, setMatch] = useState(null)
    const [contests, setContests] = useState([])
    const [myTeams, setMyTeams] = useState([])

  useEffect(() => {
    fetchMatch()
    fetchContests()
    fetchMyTeams()

    // 🔄 Auto-refresh scores and contests every 20 seconds
    const interval = setInterval(() => {
      console.log("🔄 Background refreshing match details...")
      fetchMatch()
      fetchContests()
    }, 20000)

    return () => clearInterval(interval)
  }, [])

  const fetchMyTeams = async () => {
    try {
      const user = getUser()
    const res = await API.get(`/team/my-teams?userId=${user.id || 1}&matchId=${matchId}`)
    setMyTeams(res.data.teams || [])
    } catch (err) {
      console.log(err)
    }
  }

  const fetchMatch = async () => {
    try {
      const res = await API.get(`/matches/${matchId}`)
    const matchData = res.data.data || res.data
    setMatch(Array.isArray(matchData) ? matchData[0] : matchData)
    } catch (err) {
      console.log(err)
    }
  }

  const fetchContests = async () => {
    try {
      const user = getUser()
    const res = await API.get(`/contests/match/${matchId}?userId=${user.id || 0}`)
    setContests(res.data)
    } catch (err) {
      console.log(err)
    }
  }

    if (!match) return <MatchSkeleton />

    return (
    <div className="bg-theme-bg text-theme-text min-h-screen pb-20">

      {/* 🔝 HEADER */}
      <div className="p-4 flex justify-between items-center border-b border-theme-border sticky top-0 bg-theme-card z-50 shadow-theme-sm">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 flex items-center justify-center bg-theme-bg rounded-xl border border-theme-border text-theme-text transition-all active:scale-95 shadow-theme-sm"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        <div className="text-center">
          <h2 className="font-bold text-xs uppercase tracking-tight text-theme-text">
            {match?.team1_short || match?.team1?.substring(0, 3)} vs {match?.team2_short || match?.team2?.substring(0, 3)}
          </h2>
          <p className="text-[10px] text-theme-text-sec font-bold uppercase mt-0.5 tracking-wider">
            Match Details
          </p>
        </div>

        <button onClick={() => navigate("/wallet")} className="w-10 h-10 flex items-center justify-center bg-green-500/10 rounded-xl border border-green-500/20 text-green-500 active:scale-90 transition-all">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 7H5C3.89543 7 3 7.89543 3 9V18C3 19.1046 3.89543 20 5 20H19C20.1046 20 21 19.1046 21 18V9C21 7.89543 20.1046 7 19 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M16 11V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M7 7V5C7 4.46957 7.21071 3.96086 7.58579 3.58579C7.96086 3.21071 8.46957 3 9 3H15C15.5304 3 16.0391 3.21071 16.4142 3.58579C16.7893 3.96086 17 4.46957 17 5V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      <div className="flex bg-theme-card border-b border-theme-border sticky top-[73px] z-10">
        <div className="flex-1 py-4 text-[10px] font-bold uppercase tracking-widest text-center text-accent relative">
          Browse Contests
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent"></div>
        </div>
      </div>

      <div className="p-2">
        {/* Show message if match is live and user has no team */}
        {match && parseMatchTime(match) <= new Date() && myTeams.length === 0 && (
          <div className="mx-3 mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-center">
            <p className="text-red-400 text-xs font-bold uppercase tracking-wider">⚠️ Match is Live!</p>
            <p className="text-theme-text-sec text-[10px] mt-1">You haven't created any team for this match.</p>
          </div>
        )}

        {/* 📢 AD BANNERS */}
        <div className="mx-2 mb-4">
          <BannerSlider />
        </div>

        <ContestList
          contests={contests}
          myTeams={myTeams}
          onJoin={() => { fetchContests(); fetchMyTeams(); }}
          teamIdFromUrl={teamIdToJoin}
        />
      </div>

    </div>
    )
}

    export default MatchDetails